import React from 'react';
import { Helmet } from 'react-helmet';
import { Mail, Send, Twitter, Github, MessageCircle, AlertCircle } from 'lucide-react';
import ProtectedEmail from '../components/ProtectedEmail';

const Contact = () => {
  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact FLUMBERICO",
    "description": "Get in touch with the FLUMBERICO team. Contact us for support, partnerships, or general inquiries about the world's first SEO token.",
    "url": "https://www.flumberico.fun/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "FLUMBERICO",
      "url": "https://www.flumberico.fun",
      "email": ProtectedEmail({ linkOnly: 'schema' }),
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer support",
          "email": ProtectedEmail({ linkOnly: 'schema' }),
          "availableLanguage": "English"
        },
        {
          "@type": "ContactPoint",
          "contactType": "technical support",
          "url": "https://t.me/flumberico",
          "availableLanguage": "English"
        }
      ],
      "sameAs": [
        "https://twitter.com/flumberico",
        "https://t.me/flumberico",
        "https://github.com/flumberico"
      ]
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20">
      <Helmet>
        <title>Contact FLUMBERICO - Get in Touch | Support & Inquiries</title>
        <meta name="description" content="Contact the FLUMBERICO team for support, partnerships, or inquiries. Email us or join our official Telegram channel." />
        <link rel="canonical" href="https://www.flumberico.fun/contact" />
        <script type="application/ld+json">
          {JSON.stringify(contactSchema)}
        </script>
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <MessageCircle className="w-20 h-20 text-gold mx-auto mb-6" />
          <h1 className="text-6xl font-black mb-6 text-gold">
            Contact Us
          </h1>
          <p className="text-2xl text-silver font-semibold">
            We're Here to Help
          </p>
        </div>

        {/* Primary Contact Methods */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Email */}
            <div className="bg-gradient-to-br from-gold/20 via-gold/5 to-black border-2 border-gold/40 rounded-2xl p-8 text-center">
              <Mail className="w-16 h-16 text-gold mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gold mb-3">Email Support</h2>
              <p className="text-white text-lg mb-6">
                For general inquiries, support, or partnership opportunities
              </p>
              <ProtectedEmail 
                asButton={true}
                className="inline-block font-bold text-lg px-8 py-4 rounded-full transition-all cursor-pointer"
                style={{ border: '2px solid #FFD700', color: '#FFD700', backgroundColor: 'transparent' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                  e.currentTarget.style.color = '#000000';
                  e.currentTarget.style.borderColor = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#FFD700';
                  e.currentTarget.style.borderColor = '#FFD700';
                }}
              />
              <p className="text-silver text-sm mt-4">
                Response time: 24-48 hours
              </p>
            </div>

            {/* Telegram */}
            <div className="bg-gradient-to-br from-silver/20 via-silver/5 to-black border-2 border-silver/40 rounded-2xl p-8 text-center">
              <Send className="w-16 h-16 text-silver mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-silver mb-3">Official Telegram</h2>
              <p className="text-white text-lg mb-6">
                Join our community for instant support and token claims
              </p>
              <button
                onClick={() => window.open('https://t.me/flumberico', '_blank')}
                className="inline-block font-bold text-lg px-8 py-4 rounded-full transition-all cursor-pointer"
                style={{ border: '2px solid #C0C0C0', color: '#C0C0C0', backgroundColor: 'transparent' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                  e.currentTarget.style.color = '#000000';
                  e.currentTarget.style.borderColor = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#C0C0C0';
                  e.currentTarget.style.borderColor = '#C0C0C0';
                }}
                aria-label="Join FLUMBERICO official Telegram channel"
              >
                Join @flumberico
              </button>
              <p className="text-silver text-sm mt-4">
                Active community support
              </p>
            </div>
          </div>
        </section>

        {/* Social Media */}
        <section className="mb-16 bg-gradient-to-br from-gold/10 to-black/50 border border-gold/30 rounded-lg p-8">
          <h2 className="text-3xl font-black text-gold mb-6 text-center">Connect With Us</h2>
          <p className="text-white text-lg text-center mb-8">
            Follow our social channels for updates, announcements, and community engagement
          </p>
          
          <div className="flex flex-wrap justify-center gap-6">
            {/* Twitter */}
            <a
              href="https://twitter.com/flumberico"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-black/60 border border-gold/30 rounded-lg px-6 py-4 hover:border-gold transition-all"
            >
              <Twitter size={24} className="text-gold" />
              <div>
                <p className="text-gold font-bold">Twitter / X</p>
                <p className="text-silver text-sm">@flumberico</p>
              </div>
            </a>

            {/* GitHub */}
            <a
              href="https://github.com/flumberico"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-black/60 border border-silver/30 rounded-lg px-6 py-4 hover:border-silver transition-all"
            >
              <Github size={24} className="text-silver" />
              <div>
                <p className="text-silver font-bold">GitHub</p>
                <p className="text-white/60 text-sm">Open Source</p>
              </div>
            </a>

            {/* Telegram */}
            <a
              href="https://t.me/flumberico"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-black/60 border border-gold/30 rounded-lg px-6 py-4 hover:border-gold transition-all"
            >
              <Send size={24} className="text-gold" />
              <div>
                <p className="text-gold font-bold">Telegram</p>
                <p className="text-silver text-sm">@flumberico</p>
              </div>
            </a>
          </div>
        </section>

        {/* FAQ / Quick Info */}
        <section className="mb-16">
          <h2 className="text-3xl font-black text-gold mb-6">Quick Information</h2>
          
          <div className="space-y-4">
            <div className="bg-black/60 border border-gold/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gold mb-2">🎯 For Token Claims & Backlink Submissions</h3>
              <p className="text-white mb-2">
                Join our official Telegram channel at <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-semibold">@flumberico</a>
              </p>
              <p className="text-silver text-sm">
                Submit your backlinks directly through Telegram for verification and token rewards.
              </p>
            </div>

            <div className="bg-black/60 border border-silver/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-silver mb-2">💼 For Business & Partnership Inquiries</h3>
              <div className="text-white mb-2">
                Email us at: <ProtectedEmail className="text-silver hover:underline font-semibold" />
              </div>
              <p className="text-silver text-sm">
                We're open to strategic partnerships, integrations, and collaboration opportunities.
              </p>
            </div>

            <div className="bg-black/60 border border-gold/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gold mb-2">🛠️ For Technical Support</h3>
              <div className="text-white mb-2">
                Contact our team via <ProtectedEmail className="text-gold hover:underline font-semibold" /> or <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-semibold">Telegram ↗</a>
              </div>
              <p className="text-silver text-sm">
                Include detailed information about your issue for faster resolution.
              </p>
            </div>

            <div className="bg-black/60 border border-silver/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-silver mb-2">📰 For Media & Press Inquiries</h3>
              <div className="text-white mb-2">
                Email: <ProtectedEmail className="text-silver hover:underline font-semibold" />
              </div>
              <p className="text-silver text-sm">
                For interviews, press releases, or media kit requests.
              </p>
            </div>
          </div>
        </section>

        {/* Important Notice */}
        <section className="bg-gradient-to-r from-gold/30 via-gold/10 to-black border-l-4 border-gold rounded-lg p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-bold text-gold mb-2">⚠️ Official Channels Only</h3>
              <p className="text-white mb-3">
                <span className="text-gold font-semibold">Beware of scammers!</span> We will NEVER ask you for:
              </p>
              <ul className="text-white space-y-1 ml-4 list-disc">
                <li>Your private keys or seed phrases</li>
                <li>Direct payments for token claims</li>
                <li>Personal financial information</li>
              </ul>
              <p className="text-silver text-sm mt-4">
                Only trust communications from our official email (<ProtectedEmail linkOnly="text" />) and Telegram channel (@flumberico).
              </p>
            </div>
          </div>
        </section>

        {/* Office Hours */}
        <section className="mt-12 text-center bg-black/60 border border-gold/30 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gold mb-4">Support Hours</h2>
          <p className="text-white text-lg mb-2">Monday - Friday: 9:00 AM - 6:00 PM UTC</p>
          <p className="text-silver">Weekend support available via Telegram community</p>
          <p className="text-white/60 text-sm mt-4">
            Please allow 24-48 hours for email responses
          </p>
        </section>
      </div>
    </div>
  );
};

export default Contact;
